
import { FormData, FormErrorsStep, TranslationFunction, FreeZoneScheduleData, Schedule17TaxLossData, Schedule15TaxCreditData, Schedule19InterestCappingData, Schedule12IPData, Schedule13UaeDividendsData, Schedule14ForeignPEData, Schedule16RelatedPartyData, Schedule16ConnectedPersonsData, Schedule17TaxGroupLossData, Schedule18ParticipationExemptionData, Schedule20ReliefData, Schedule21TransitionalRulesData, Schedule22OtherSubScheduleData, Schedule22_4_AttachmentsData, AttachmentItem, UnincorporatedPartnershipItem, BusinessActivityItem, RevenuePercentageItem, TaxablePersonDetails, UboDetail, TaxGroupMemberItem } from '../types';
import { safeParseFloat, getAccountingBasis } from './calculations';

export const isNotEmpty = (value: string | undefined | null): boolean => {
  return value !== undefined && value !== null && String(value).trim() !== '';
};

export const isNumber = (value: string | undefined | null): boolean => {
  if (!isNotEmpty(value)) return true; // Allow empty to be handled by isNotEmpty if required
  return !isNaN(Number(value));
};

export const isPositiveNumberOrZero = (value: string | undefined | null): boolean => {
    if (value === undefined || value === null || String(value).trim() === '') return true; 
    if (!isNumber(value)) return false; 
    return parseFloat(String(value)!) >=0;
}
export const isPositiveNumber = (value: string | undefined | null): boolean => {
    if (value === undefined || value === null || String(value).trim() === '') return true;
    if (!isNumber(value)) return false;
    return parseFloat(String(value)!) > 0;
}

export const isPercentage = (value: string | undefined | null): boolean => {
    if (!isNotEmpty(value)) return true;
    if (!isNumber(value)) return false;
    const num = parseFloat(String(value)!);
    return num >= 0 && num <= 100;
}

export const isValidTRN = (trn: string | undefined | null): boolean => {
    if (!isNotEmpty(trn)) return false; // TRN is mandatory for validation
    return String(trn)!.length === 15 && /^\d+$/.test(String(trn)!);
}


export const isValidDate = (dateString: string | undefined | null): boolean => {
  if (!isNotEmpty(dateString)) return false;
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(String(dateString)!)) return false;
  const date = new Date(String(dateString)!);
  const [year, month, day] = String(dateString)!.split('-').map(Number);
  return date instanceof Date && 
         !isNaN(date.getTime()) && 
         date.getFullYear() === year &&
         date.getMonth() + 1 === month &&
         date.getDate() === day;
};

export const isValidEmail = (email: string | undefined | null): boolean => {
  if (!isNotEmpty(email)) return false;
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(String(email)!);
};

export const isValidMobile = (mobile: string | undefined | null): boolean => {
  if (!isNotEmpty(mobile)) return false;
  const regex = /^[+\d\s()-]*\d[+\d\s()-]*$/; 
  return regex.test(String(mobile)!);
};

// Helper function to manage errors for common nested blocks within specifics
const validateSpecificsSubBlock = (
    specifics: any, 
    specificErrorsContainer: FormErrorsStep, 
    t: TranslationFunction
) => {
    if ('isPartnerInUnincorporatedPartnerships' in specifics || 'isPartnerInOtherUPs' in specifics) {
        const partnerKey = 'isPartnerInUnincorporatedPartnerships' in specifics ? 'isPartnerInUnincorporatedPartnerships' : 'isPartnerInOtherUPs';
        const upListKey = 'isPartnerInUnincorporatedPartnerships' in specifics ? 'unincorporatedPartnerships' : 'otherUPs';
        const upLabel = 'isPartnerInUnincorporatedPartnerships' in specifics ? t('is_partner_label') : t('up_is_partner_in_other_up_label');

        if (!isNotEmpty(specifics[partnerKey])) {
            specificErrorsContainer[partnerKey] = t('validation_required_select', { field: upLabel });
        } else if (specifics[partnerKey] === 'yes') {
            const upArrayErrors: FormErrorsStep[] = [];
            let hasUPListErrors = false;
            const upList = specifics[upListKey] || [];
            if (upList.length === 0 || (upList.length === 1 && !isNotEmpty(upList[0].name) && !isNotEmpty(upList[0].trn))) {
                 (specificErrorsContainer as any)[`${upListKey}Overall`] = t('validation_at_least_one_item_required', {listName: t('unincorporated_partnership_details_label')});
            } else {
                upList.forEach((up: UnincorporatedPartnershipItem, index: number) => {
                    const upError: FormErrorsStep = {};
                    if (!isNotEmpty(up.name)) upError.name = t('validation_required', { field: `${t('unincorporated_partnership_name_label')} ${index + 1}` });
                    if (!isNotEmpty(up.trn)) upError.trn = t('validation_required', { field: `${t('partner_trn_label')} ${index + 1}` });
                    else if(!isValidTRN(up.trn)) upError.trn = t('validation_trn_format');
                    upArrayErrors[index] = upError;
                    if (Object.keys(upError).length > 0) hasUPListErrors = true;
                });
                if (hasUPListErrors) {
                    specificErrorsContainer[upListKey] = upArrayErrors;
                }
            }
        }
    }
    if ('conductsMultipleBusinesses' in specifics) {
        if (!isNotEmpty(specifics.conductsMultipleBusinesses)) {
            specificErrorsContainer.conductsMultipleBusinesses = t('validation_required_select', {field: t('conducts_multiple_businesses_label')});
        } else if (specifics.conductsMultipleBusinesses === 'yes') {
            if (!isNotEmpty(specifics.businessActivitiesCount) || parseInt(specifics.businessActivitiesCount, 10) < 1) {
                specificErrorsContainer.businessActivitiesCount = t('validation_positive_integer_required', { field: t('business_activities_count_label') });
            }
            const activityErrorsArray: FormErrorsStep[] = [];
            let hasActivityErrors = false;
            const activityList = specifics.businessActivityList || [];
            if (activityList.length === 0 || (activityList.length === 1 && !isNotEmpty(activityList[0].activity))) {
                specificErrorsContainer.businessActivityListOverall = t('validation_at_least_one_item_required', {listName: t('business_activity_list_label')});
            } else {
                activityList.forEach((act: BusinessActivityItem, index: number) => {
                    const actError: FormErrorsStep = {};
                    if (!isNotEmpty(act.activity)) {
                        actError.activity = t('validation_field_required_indexed', { field: t('activity_label'), index: index + 1 });
                    }
                    activityErrorsArray[index] = actError;
                    if(Object.keys(actError).length > 0) hasActivityErrors = true;
                });
                if (hasActivityErrors) specificErrorsContainer.businessActivityList = activityErrorsArray;
            }
        }
    }

    if ('revenuePercentageBySector' in specifics) {
        const sectorErrorsArray: FormErrorsStep[] = [];
        let totalPercentage = 0;
        let hasSectorErrors = false;
        const sectorList = specifics.revenuePercentageBySector || [];
        if (sectorList.length === 0 || (sectorList.length === 1 && (!isNotEmpty(sectorList[0].sector) && !isNotEmpty(sectorList[0].percentage)))) {
            specificErrorsContainer.revenuePercentageBySectorOverall = t('validation_at_least_one_item_required', {listName: t('revenue_percentage_by_sector_label')});
        } else {
            sectorList.forEach((rps: RevenuePercentageItem, index: number) => {
                const rpsError: FormErrorsStep = {};
                if (!isNotEmpty(rps.sector)) rpsError.sector = t('validation_field_required_indexed', { field: t('sector_label'), index: index + 1 });
                if (!isNotEmpty(rps.percentage)) {
                    rpsError.percentage = t('validation_field_required_indexed', { field: t('percentage_label'), index: index + 1 });
                } else if (!isPercentage(rps.percentage)) {
                    rpsError.percentage = t('validation_percentage_invalid');
                } else {
                    totalPercentage += parseFloat(rps.percentage);
                }
                sectorErrorsArray[index] = rpsError;
                if (Object.keys(rpsError).length > 0) hasSectorErrors = true;
            });

            if (sectorList.length > 0 && Math.round(totalPercentage) !== 100) {
                specificErrorsContainer.revenuePercentageTotal = t('validation_percentage_sum');
            }
            if (hasSectorErrors) specificErrorsContainer.revenuePercentageBySector = sectorErrorsArray;
        }
    }
};


export const validateStep1 = (data: FormData, t: TranslationFunction): FormErrorsStep => {
  const errors: FormErrorsStep = {}; 
  const details = data.taxablePersonDetails;

  if (!isNotEmpty(details.filingAs)) {
    errors.filingAs = t('validation_required_select', { field: t('filing_as_label')});
    return errors; // Stop further validation if filingAs is not selected
  }
  if (!isNotEmpty(details.taxablePersonName)) {
    errors.taxablePersonName = t('validation_required', { field: t('taxable_person_name_label')});
  }
  if (!isValidTRN(details.trn)) {
    errors.trn = t('validation_trn_format');
  }
   if (details.filingAs === 'exempt_person') {
       if (!isNotEmpty(details.isExemptPerson)) { 
        errors.isExemptPerson = t('validation_required_select', { field: t('is_exempt_person_label')});
      } else if (details.isExemptPerson === 'yes' && !isNotEmpty(details.exemptPersonHasTaxableBusiness)) {
        errors.exemptPersonHasTaxableBusiness = t('validation_required_select', { field: t('exempt_person_has_taxable_business_label')});
      }
      // If exempt and no taxable business, most other fields are not relevant
      if (details.isExemptPerson === 'yes' && details.exemptPersonHasTaxableBusiness === 'no') {
        return errors;
      }
   }
  if (!isNotEmpty(details.primaryBusinessActivity)) {
    errors.primaryBusinessActivity = t('validation_required', { field: t('primary_business_activity_label')});
  }

  const addressErrors: FormErrorsStep = {};
  if (!isNotEmpty(details.mainAddress.addressLine1)) { addressErrors.addressLine1 = t('validation_required', { field: t('address_line1_label')}); }
  if (!isNotEmpty(details.mainAddress.city)) { addressErrors.city = t('validation_required_select', { field: t('city_label')}); }
  if (!isNotEmpty(details.mainAddress.emirateOrState)) { addressErrors.emirateOrState = t('validation_required_select', { field: t('emirate_state_label')});}
  if (!isNotEmpty(details.mainAddress.country)) { addressErrors.country = t('validation_required_select', { field: t('country_label')}); }
  if (Object.keys(addressErrors).length > 0) { errors.mainAddress = addressErrors; }

  const contactErrors: FormErrorsStep = {};
  if (!isNotEmpty(details.contactPerson.contactName)) { contactErrors.contactName = t('validation_required', { field: t('contact_name_label')}); }
  if (!isNotEmpty(details.contactPerson.contactEmail)) { contactErrors.contactEmail = t('validation_required', { field: t('contact_email_label')}); } 
  else if (!isValidEmail(details.contactPerson.contactEmail)) { contactErrors.contactEmail = t('validation_email_format', { field: t('contact_email_label')});}
  if (!isNotEmpty(details.contactPerson.contactMobile)) { contactErrors.contactMobile = t('validation_required', { field: t('contact_mobile_label')}); } 
  else if (!isValidMobile(details.contactPerson.contactMobile)) { contactErrors.contactMobile = t('validation_mobile_format', { field: t('contact_mobile_label')});}
  if (Object.keys(contactErrors).length > 0) { errors.contactPerson = contactErrors; }

  if (!isNotEmpty(details.taxPeriodStartDate) || !isValidDate(details.taxPeriodStartDate)) { errors.taxPeriodStartDate = t('validation_date_format', { field: t('tax_period_start_date_label')}); }
  if (!isNotEmpty(details.taxPeriodEndDate) || !isValidDate(details.taxPeriodEndDate)) { errors.taxPeriodEndDate = t('validation_date_format', { field: t('tax_period_end_date_label')}); } 
  else if (isValidDate(details.taxPeriodStartDate) && isValidDate(details.taxPeriodEndDate) && new Date(details.taxPeriodEndDate) < new Date(details.taxPeriodStartDate)) {
    errors.taxPeriodEndDate = t('validation_date_order', { field1: t('tax_period_end_date_label'), field2: t('tax_period_start_date_label')});
  }
  
  if (details.filingAs === 'natural_person') {
    const npSpecificErrors: FormErrorsStep = {};
    const npSpecifics = details.naturalPersonSpecifics;
    if (!isNotEmpty(npSpecifics.uaeResidencyStatus)) npSpecificErrors.uaeResidencyStatus = t('validation_required_select', { field: t('natural_person_uae_residency_status_label')});
    if (!isNotEmpty(npSpecifics.nationality)) npSpecificErrors.nationality = t('validation_required_select', { field: t('natural_person_nationality_label')});
    if (!isNotEmpty(npSpecifics.turnover)) npSpecificErrors.turnover = t('validation_required', { field: t('turnover_label')});
    else if (!isPositiveNumberOrZero(npSpecifics.turnover)) npSpecificErrors.turnover = t('validation_positive_number_or_zero', { field: t('turnover_label')});
    if (!isNotEmpty(npSpecifics.accountingBasis)) npSpecificErrors.accountingBasis = t('validation_required_select', { field: t('accounting_basis_label')});
    if (!isNotEmpty(npSpecifics.taxResidencyStatusDTA)) npSpecificErrors.taxResidencyStatusDTA = t('validation_required_select', { field: t('tax_residency_status_dta_label')});
    else if(npSpecifics.taxResidencyStatusDTA === 'non-resident' && !isNotEmpty(npSpecifics.nonResidentCountryDTA)) npSpecificErrors.nonResidentCountryDTA = t('validation_required_select', { field: t('dta_country_label')});
    validateSpecificsSubBlock(npSpecifics, npSpecificErrors, t);
    if (Object.keys(npSpecificErrors).length > 0) errors.naturalPersonSpecifics = npSpecificErrors;
  } else if (details.filingAs === 'juridical_person' || details.filingAs === 'free_zone_person') {
    const jpSpecificErrors: FormErrorsStep = {};
    const jpSpecifics = details.juridicalPersonSpecifics;
    if (!isNotEmpty(jpSpecifics.incorporationDate) || !isValidDate(jpSpecifics.incorporationDate)) jpSpecificErrors.incorporationDate = t('validation_date_format', { field: t('juridical_person_incorporation_date_label')});
    if (!isNotEmpty(jpSpecifics.isUaeOrFzIncorporated)) jpSpecificErrors.isUaeOrFzIncorporated = t('validation_required_select', { field: t('is_uae_or_fz_incorporated_label')});
    else if (jpSpecifics.isUaeOrFzIncorporated === 'yes') {
        if(!isNotEmpty(jpSpecifics.isTaxResidentInForeignJurisdictionUnderDTA)) jpSpecificErrors.isTaxResidentInForeignJurisdictionUnderDTA = t('validation_required_select', { field: t('jp_dta_resident_foreign_label')});
        else if (jpSpecifics.isTaxResidentInForeignJurisdictionUnderDTA === 'yes') {
            if(!isNotEmpty(jpSpecifics.dtaForeignJurisdictionCountry)) jpSpecificErrors.dtaForeignJurisdictionCountry = t('validation_required_select', { field: t('jp_dta_foreign_country_label')});
            if(!isNotEmpty(jpSpecifics.isNonResidentInUaeUnderDTA)) jpSpecificErrors.isNonResidentInUaeUnderDTA = t('validation_required_select', { field: t('jp_dta_is_non_resident_label')});
        }
    } else { // Foreign incorporated
        if(!isNotEmpty(jpSpecifics.incorporationCountry)) jpSpecificErrors.incorporationCountry = t('validation_required_select', { field: t('juridical_person_incorporation_country_label')});
        if(!isNotEmpty(jpSpecifics.isForeignIncorporatedUaeControlled)) jpSpecificErrors.isForeignIncorporatedUaeControlled = t('validation_required_select', { field: t('is_foreign_incorporated_uae_controlled_label')});
        else if (jpSpecifics.isForeignIncorporatedUaeControlled === 'yes') {
            if(!isNotEmpty(jpSpecifics.isTaxResidentInForeignJurisdictionUnderDTA)) jpSpecificErrors.isTaxResidentInForeignJurisdictionUnderDTA = t('validation_required_select', { field: t('jp_dta_resident_foreign_label')});
            else if (jpSpecifics.isTaxResidentInForeignJurisdictionUnderDTA === 'yes' && !isNotEmpty(jpSpecifics.dtaForeignJurisdictionCountry)) jpSpecificErrors.dtaForeignJurisdictionCountry = t('validation_required_select', { field: t('jp_dta_foreign_country_label')});
        }
    }
    const isJpNonResidentAndRequiresBasis = 
        (jpSpecifics.isUaeOrFzIncorporated === 'yes' && jpSpecifics.isTaxResidentInForeignJurisdictionUnderDTA === 'yes' && jpSpecifics.isNonResidentInUaeUnderDTA === 'yes') ||
        (jpSpecifics.isUaeOrFzIncorporated === 'no' && jpSpecifics.isForeignIncorporatedUaeControlled === 'no') ||
        (jpSpecifics.isUaeOrFzIncorporated === 'no' && jpSpecifics.isForeignIncorporatedUaeControlled === 'yes' && jpSpecifics.isTaxResidentInForeignJurisdictionUnderDTA === 'yes');

    if (isJpNonResidentAndRequiresBasis && !isNotEmpty(jpSpecifics.nonResidentFilingBasis)) {
        jpSpecificErrors.nonResidentFilingBasis = t('validation_required_select', { field: t('non_resident_juridical_filing_basis_label')});
    }
    if (!isNotEmpty(jpSpecifics.isBankOrInsuranceProvider)) jpSpecificErrors.isBankOrInsuranceProvider = t('validation_required_select', { field: t('is_bank_or_insurance_provider_label')});
    if (!isNotEmpty(jpSpecifics.isMneGroupMember)) jpSpecificErrors.isMneGroupMember = t('validation_required_select', { field: t('is_mne_group_member_label') });
    if (!isNotEmpty(jpSpecifics.revenueForTaxPeriod)) jpSpecificErrors.revenueForTaxPeriod = t('validation_required', { field: t('revenue_for_tax_period_label')});
    else if (!isPositiveNumberOrZero(jpSpecifics.revenueForTaxPeriod)) jpSpecificErrors.revenueForTaxPeriod = t('validation_positive_number_or_zero', { field: t('revenue_for_tax_period_label')});
    if (!isNotEmpty(jpSpecifics.accountingBasis)) jpSpecificErrors.accountingBasis = t('validation_required_select', { field: t('accounting_basis_label')});
    if (!isNotEmpty(jpSpecifics.isGovernmentEntityOrAffiliate)) jpSpecificErrors.isGovernmentEntityOrAffiliate = t('validation_required_select', { field: t('is_govt_entity_label')});
    else if (jpSpecifics.isGovernmentEntityOrAffiliate === 'yes' && !isNotEmpty(jpSpecifics.conductsTaxableActivityIfGovt)) {
        jpSpecificErrors.conductsTaxableActivityIfGovt = t('validation_required_select', { field: t('govt_entity_taxable_activity_label')});
    }

    if (details.filingAs === 'free_zone_person') {
        if(!isNotEmpty(jpSpecifics.electionToForego0RateFZ)) jpSpecificErrors.electionToForego0RateFZ = t('validation_required_select', { field: t('fz_election_forego_0_rate_label')});
        else if (jpSpecifics.electionToForego0RateFZ === 'no' && !isNotEmpty(jpSpecifics.meetsQualifyingFZRequirements)) {
            jpSpecificErrors.meetsQualifyingFZRequirements = t('validation_required_select', { field: t('fz_meets_requirements_label')});
        }
    } else if (details.filingAs === 'juridical_person') {
        if (!isNotEmpty(jpSpecifics.isFreeZonePerson)) {
          jpSpecificErrors.isFreeZonePerson = t('validation_required_select', { field: t('is_fz_person_confirm_label') });
        } else if (jpSpecifics.isFreeZonePerson === 'yes') {
            if(!isNotEmpty(jpSpecifics.electionToForego0RateFZ)) jpSpecificErrors.electionToForego0RateFZ = t('validation_required_select', { field: t('fz_election_forego_0_rate_label')});
            else if (jpSpecifics.electionToForego0RateFZ === 'no' && !isNotEmpty(jpSpecifics.meetsQualifyingFZRequirements)) {
                jpSpecificErrors.meetsQualifyingFZRequirements = t('validation_required_select', { field: t('fz_meets_requirements_label')});
            }
        }
    }
    validateSpecificsSubBlock(jpSpecifics, jpSpecificErrors, t);
    if (Object.keys(jpSpecificErrors).length > 0) errors.juridicalPersonSpecifics = jpSpecificErrors;
  } else if (details.filingAs === 'unincorporated_partnership') {
    const upSpecificErrors: FormErrorsStep = {};
    const upSpecifics = details.unincorporatedPartnershipSpecifics;
    if(!isNotEmpty(upSpecifics.isMneGroupMember)) upSpecificErrors.isMneGroupMember = t('validation_required_select', { field: t('is_mne_group_member_label')});
    if(!isNotEmpty(upSpecifics.revenueForTaxPeriod) || !isPositiveNumberOrZero(upSpecifics.revenueForTaxPeriod)) upSpecificErrors.revenueForTaxPeriod = t('validation_required_positive_number_or_zero', { field: t('revenue_for_tax_period_label')});
    if(!isNotEmpty(upSpecifics.accountingBasis)) upSpecificErrors.accountingBasis = t('validation_required_select', { field: t('accounting_basis_label')});
    validateSpecificsSubBlock(upSpecifics, upSpecificErrors, t);
    if (Object.keys(upSpecificErrors).length > 0) errors.unincorporatedPartnershipSpecifics = upSpecificErrors;
  } else if (details.filingAs === 'tax_group_parent') {
    const tgSpecificErrors: FormErrorsStep = {};
    const tgSpecifics = details.taxGroupSpecifics;
    if(!isNotEmpty(tgSpecifics.taxGroupName)) tgSpecificErrors.taxGroupName = t('validation_required', { field: t('tax_group_name_label')});
    if(!isNotEmpty(tgSpecifics.parentCompanyTrn) || !isValidTRN(tgSpecifics.parentCompanyTrn)) tgSpecificErrors.parentCompanyTrn = t('validation_trn_format_for_field', { field: t('parent_company_trn_label')});
    if(!tgSpecifics.members || tgSpecifics.members.length === 0 || (tgSpecifics.members.length === 1 && (!isNotEmpty(tgSpecifics.members[0].name) && !isNotEmpty(tgSpecifics.members[0].trn)))) {
      (tgSpecificErrors as any).membersOverall = t('validation_at_least_one_item_required', {listName: t('tax_group_members_label')});
    } else {
      const memberErrorsArray: FormErrorsStep[] = [];
      let hasMemberErrors = false;
      tgSpecifics.members.forEach((member: TaxGroupMemberItem, index: number) => {
        const memberError: FormErrorsStep = {};
        if(!isNotEmpty(member.name)) memberError.name = t('validation_field_required_indexed', {field: t('tax_group_member_name_label'), index: index + 1});
        if(!isNotEmpty(member.trn) || !isValidTRN(member.trn)) memberError.trn = t('validation_trn_format_for_field_indexed', {field: t('tax_group_member_trn_label'), index: index+1});
        memberErrorsArray[index] = memberError;
        if (Object.keys(memberError).length > 0) hasMemberErrors = true;
      });
      if (hasMemberErrors) tgSpecificErrors.members = memberErrorsArray;
    }
    if(!isNotEmpty(tgSpecifics.isMneGroupMember)) tgSpecificErrors.isMneGroupMember = t('validation_required_select', { field: t('is_mne_group_member_label')});
    if(!isNotEmpty(tgSpecifics.consolidatedRevenue) || !isPositiveNumberOrZero(tgSpecifics.consolidatedRevenue)) tgSpecificErrors.consolidatedRevenue = t('validation_required_positive_number_or_zero', { field: t('tax_group_consolidated_revenue_label')});
    if(!isNotEmpty(tgSpecifics.accountingBasis)) tgSpecificErrors.accountingBasis = t('validation_required_select', { field: t('accounting_basis_label')});
    validateSpecificsSubBlock(tgSpecifics, tgSpecificErrors, t);
    if (Object.keys(tgSpecificErrors).length > 0) errors.taxGroupSpecifics = tgSpecificErrors;
  }

  if (!isNotEmpty(details.isUboInfoDisclosed)) {
    errors.isUboInfoDisclosed = t('validation_required_select', { field: t('is_ubo_info_disclosed_label')});
  } else if (details.isUboInfoDisclosed === 'yes') {
    if (!details.uboDetails || details.uboDetails.length === 0 || (details.uboDetails.length === 1 && !isNotEmpty(details.uboDetails[0].uboName) && !isNotEmpty(details.uboDetails[0].uboTrnTaxId) && !isNotEmpty(details.uboDetails[0].uboNationality) && !isNotEmpty(details.uboDetails[0].uboCountryOfResidence))) {
      errors.uboDetailsOverall = t('validation_at_least_one_ubo'); 
    } else {
      const uboDetailErrorsArray: FormErrorsStep[] = [];
      let hasUboErrors = false;
      details.uboDetails.forEach((ubo, index) => {
        const uboError: FormErrorsStep = {};
        if (!isNotEmpty(ubo.uboName)) uboError.uboName = t('validation_ubo_field_required', { field: t('ubo_name_label'), index: index + 1 });
        if (!isNotEmpty(ubo.uboTrnTaxId)) uboError.uboTrnTaxId = t('validation_ubo_field_required', { field: t('ubo_trn_tax_id_label'), index: index + 1 });
        // TRN/Tax ID format validation can be complex depending on type. Basic check for now.
        if (!isNotEmpty(ubo.uboNationality)) uboError.uboNationality = t('validation_required_select_for_field_indexed', { field: t('ubo_nationality_label'), index: index + 1 });
        if (!isNotEmpty(ubo.uboCountryOfResidence)) uboError.uboCountryOfResidence = t('validation_required_select_for_field_indexed', { field: t('ubo_country_of_residence_label'), index: index + 1 });
        
        uboDetailErrorsArray[index] = uboError; 
        if (Object.keys(uboError).length > 0) { hasUboErrors = true; }
      });
      if (hasUboErrors) { errors.uboDetails = uboDetailErrorsArray; }
    }
  }
  return errors;
};

export const validateStep2 = (data: FormData, t: TranslationFunction): FormErrorsStep => {
  const errors: FormErrorsStep = {};
  const elections = data.elections;
  const taxablePersonDetails = data.taxablePersonDetails;
  const accountingBasis = getAccountingBasis(taxablePersonDetails);
  const isFirstTaxPeriod = true; // Placeholder

  if (elections.realisationBasis && accountingBasis === 'cash') {
    errors.realisationBasis = t('validation_realisation_basis_cash_disabled');
  }
  if (elections.realisationBasis && !isFirstTaxPeriod) errors.realisationBasis = t('election_first_period_only');
  if (elections.realisationBasis && taxablePersonDetails.juridicalPersonSpecifics?.isBankOrInsuranceProvider === 'yes' && !isNotEmpty(elections.realisationBasisBankOption)) {
    errors.realisationBasisBankOption = t('validation_required_select', {field: t('realisation_basis_bank_option_label')});
  }

  const transitionalRulesSelected = elections.transitionalRulesImmovableProperty || elections.transitionalRulesIntangibleAssets || elections.transitionalRulesFinancialAssetsLiabilities;
  if (transitionalRulesSelected && (accountingBasis === 'cash' || !isFirstTaxPeriod)) {
    errors.transitionalRulesGeneric = t('validation_transitional_rules_conditions_not_met_generic');
  }
  
  if (elections.transfersWithinQualifyingGroup && elections.hasTransfersWithinQGInPeriod === undefined) {
       errors.hasTransfersWithinQGInPeriod = t('validation_required_select', {field: t('election_qg_transfer_occurred_label')});
  }
  if (elections.businessRestructuringRelief && elections.hasBusinessRestructuringInPeriod === undefined) {
      errors.hasBusinessRestructuringInPeriod = t('validation_required_select', {field: t('election_brr_transfer_occurred_label')});
  }
  if (elections.foreignPermanentEstablishmentExemption && elections.hasForeignPEs !== true) { // Note: hasForeignPEs is boolean
    errors.foreignPermanentEstablishmentExemption = t('validation_election_consistency_fpe');
  }
  if(elections.hasForeignPEs === false && elections.foreignPermanentEstablishmentExemption) {
    errors.foreignPermanentEstablishmentExemption = t('validation_fpe_no_pes');
  }

  return errors;
};

export const validateStep3 = (data: FormData, t: TranslationFunction): FormErrorsStep => {
  const errors: FormErrorsStep = {};
  const acc = data.accounting;
  const accountingBasis = getAccountingBasis(data.taxablePersonDetails);

  if (!isNotEmpty(acc.accountingIncome) || !isNumber(acc.accountingIncome)) { errors.accountingIncome = t('validation_number_required', { field: t('accounting_income_label') }); }
  if (!isNotEmpty(acc.financialStatementsAudited)) { errors.financialStatementsAudited = t('validation_required_select', { field: t('financial_statements_audited_label') }); } 
  else if (acc.financialStatementsAudited === 'yes') {
    if (!isNotEmpty(acc.auditOpinion)) { errors.auditOpinion = t('validation_required_select', { field: t('audit_opinion_label') }); }
    if (!isNotEmpty(acc.auditorName)) { errors.auditorName = t('validation_required', { field: t('auditor_name_label') }); }
  }

  const incomeStatementFields: (keyof typeof acc)[] = ['incomeStatement_revenue', 'incomeStatement_costOfSales', 'incomeStatement_grossProfit', 'incomeStatement_operatingExpenses', 'incomeStatement_otherIncome'];
  incomeStatementFields.forEach(field => {
    if (!isNotEmpty(acc[field]) || !isNumber(acc[field])) {
        errors[field] = t('validation_number_required', { field: t(`accounting_${field}_label` as any) || field });
    }
  });
  if (accountingBasis !== 'cash') {
      const ociBalanceFields: (keyof typeof acc)[] = ['oci_itemsNetOfTax', 'balanceSheet_totalAssets', 'balanceSheet_totalLiabilities', 'balanceSheet_totalEquity'];
      ociBalanceFields.forEach(field => {
        if (!isNotEmpty(acc[field]) || !isNumber(acc[field])) {
            errors[field] = t('validation_number_required', { field: t(`accounting_${field}_label` as any) || field });
        }
      });
  }
  return errors;
};

export const validateStep4 = (data: FormData, t: TranslationFunction): FormErrorsStep => {
  const errors: FormErrorsStep = {};
  // Basic validation for all numeric fields to ensure they are numbers if filled
  Object.entries(data.accountingAdjustments).forEach(([key, value]) => {
    if(typeof value === 'string' && isNotEmpty(value) && !isNumber(value) && 
        !key.endsWith('Desc') && !key.endsWith('Country') && 
        !key.endsWith('InvestmentAccounting') && !key.endsWith('SeparateTP') &&
        !key.endsWith('NotInISRecognised') && !key.endsWith('FromUaeResident') &&
        !key.endsWith('FromParticipatingInterest') && !key.endsWith('AircraftShippingNonResident')
      ) {
      (errors as any)[key] = t('validation_must_be_number_if_provided', {field: t(`${key}_label` as any) || key });
    }
  });
  return errors;
};

export const validateStep5 = (data: FormData, t: TranslationFunction): FormErrorsStep => {
  const errors: FormErrorsStep = {};
   Object.entries(data.relief).forEach(([key, value]) => {
    if(typeof value === 'string' && isNotEmpty(value) && !isNumber(value) && 
        !key.endsWith('Desc') && !key.endsWith('Needed')
      ) {
      (errors as any)[key] = t('validation_must_be_number_if_provided', {field: t(`${key}_label` as any) || key });
    }
  });
  return errors;
};

export const validateStep6 = (data: FormData, t: TranslationFunction): FormErrorsStep => {
  const errors: FormErrorsStep = {};
  const other = data.otherAdjustments;
  if(isNotEmpty(other.nonDeductibleEntertainmentExp) && !isPositiveNumberOrZero(other.nonDeductibleEntertainmentExp)) errors.nonDeductibleEntertainmentExp = t('validation_positive_number_or_zero', {field: t('s9_1_entertainment_exp_label')});
  if(isNotEmpty(other.nonDeductiblePensionContributions) && !isPositiveNumberOrZero(other.nonDeductiblePensionContributions)) errors.nonDeductiblePensionContributions = t('validation_positive_number_or_zero', {field: t('s9_1_pension_contributions_label')});
  // ... Add more for each numeric field in step 6
  if (other.relatedPartyTransactionsExist === 'yes' && !isNotEmpty(other.relatedPartyTransactionsAggregateValueExceeds40M)) {
    errors.relatedPartyTransactionsAggregateValueExceeds40M = t('validation_required_select', {field: t('s9_3_related_party_aggregate_exceeds_40m_label')});
  }
  if (other.priorPeriodErrorCorrectionImpactAED10000OrLess === 'yes') {
    if(!other.priorPeriodErrorAdjustmentsTaxPeriods || other.priorPeriodErrorAdjustmentsTaxPeriods.length === 0) errors.priorPeriodErrorAdjustmentsTaxPeriods = t('validation_required_select_multiple', {field: t('s9_5_prior_period_error_tax_periods_label')});
    if(!isNotEmpty(other.priorPeriodErrorAdjustmentsIncreaseTI) && !isNotEmpty(other.priorPeriodErrorAdjustmentsDecreaseTI)) errors.priorPeriodErrorAdjustmentsGeneric = t('validation_prior_period_error_amount_required');
  }
  return errors;
};

export const validateStep7 = (data: FormData, t: TranslationFunction): FormErrorsStep => {
  const errors: FormErrorsStep = {};
  if(!isNotEmpty(data.taxLiability.hasBroughtForwardTaxLosses)) errors.hasBroughtForwardTaxLosses = t('validation_required_select', {field: t('s10_2_has_brought_forward_losses_label')});
  if(!isNotEmpty(data.taxLiability.wishToUseTaxCredits)) errors.wishToUseTaxCredits = t('validation_required_select', {field: t('s10_4_wish_to_use_credits_label')});
  if(data.taxLiability.estimatedFiguresIncludedInReturn === 'yes' && !isNotEmpty(data.taxLiability.estimatedFiguresDetails)){
    errors.estimatedFiguresDetails = t('validation_required', {field: t('s10_5_details_label')});
  }
  return errors;
};

export const validateFreeZoneSchedule = (data: FreeZoneScheduleData, t: TranslationFunction, fullFormData?: FormData): FormErrorsStep => {
  const errors: FormErrorsStep = {};
  // Revenue Schedule
  if(!isNotEmpty(data.totalRevenueFinancialStatements) || !isNumber(data.totalRevenueFinancialStatements)) errors.totalRevenueFinancialStatements = t('validation_number_required', {field: t('fz_A1_total_revenue_fs')});
  if(!isNotEmpty(data.hasDomesticOrForeignPE)) errors.hasDomesticOrForeignPE = t('validation_required_select', {field: t('fz_A2_has_pe')});
  else if(data.hasDomesticOrForeignPE === 'yes' && (!isNotEmpty(data.revenueAttrToPE) || !isNumber(data.revenueAttrToPE))) errors.revenueAttrToPE = t('validation_number_required', {field: t('fz_A3_revenue_attr_pe')});
  // ... more for revenue section
  
  // Non-Qualifying Revenue
  if(!isNotEmpty(data.revenueExcludedActivities) || !isNumber(data.revenueExcludedActivities)) errors.revenueExcludedActivities = t('validation_number_required', {field: t('fz_B1_revenue_excluded_activities')});
  // ... more for non-qualifying revenue

  // Substance
  if(!isNotEmpty(data.allEmployeesInFz)) errors.allEmployeesInFz = t('validation_required_select', {field: t('fz_E1_all_employees_in_fz')});
  if(!isNotEmpty(data.avgNumFullTimeEmployees) || !isPositiveNumberOrZero(data.avgNumFullTimeEmployees)) errors.avgNumFullTimeEmployees = t('validation_positive_number_or_zero', {field: t('fz_E2_avg_num_employees')});
  if(!isNotEmpty(data.avgNumFullTimeEmployeesInFz) || !isPositiveNumberOrZero(data.avgNumFullTimeEmployeesInFz)) errors.avgNumFullTimeEmployeesInFz = t('validation_positive_number_or_zero', {field: t('fz_E3_avg_num_employees_in_fz')});
  else if(safeParseFloat(data.avgNumFullTimeEmployeesInFz) > safeParseFloat(data.avgNumFullTimeEmployees)) errors.avgNumFullTimeEmployeesInFz = t('validation_fz_employees_in_fz_max');
  // ... more for substance

  // Confirmations
  if(!data.adequateAssetsInFz) errors.adequateAssetsInFz = t('validation_confirmation_required', {field: t('fz_F1_adequate_assets')});
  // ... more confirmations

  // Additional Info - Emirate Breakdown
  if (!data.emirateBreakdown || data.emirateBreakdown.length === 0 || (data.emirateBreakdown.length === 1 && !isNotEmpty(data.emirateBreakdown[0].emirate))) {
    (errors as any).emirateBreakdownOverall = t('validation_at_least_one_item_required', { listName: t('fz_G1_emirate_breakdown_label') });
  } else {
    const emirateErrorsArray: FormErrorsStep[] = [];
    data.emirateBreakdown.forEach((item, index) => {
        const itemError: FormErrorsStep = {};
        if(!isNotEmpty(item.emirate)) itemError.emirate = t('validation_field_required_indexed', {field: t('fz_emirate_name_label'), index: index+1});
        if(!isNotEmpty(item.ebitda) || !isNumber(item.ebitda)) itemError.ebitda = t('validation_number_required_indexed', {field: t('fz_ebitda_label'), index: index+1});
        if(!isNotEmpty(item.salariesAndWages) || !isNumber(item.salariesAndWages)) itemError.salariesAndWages = t('validation_number_required_indexed', {field: t('fz_salaries_wages_label'), index: index+1});
        if(Object.keys(itemError).length > 0) emirateErrorsArray[index] = itemError;
    });
    if(emirateErrorsArray.some(e => Object.keys(e).length > 0)) errors.emirateBreakdown = emirateErrorsArray;
  }

  return errors;
};
export const validateSchedule12IP = (data: Schedule12IPData, t: TranslationFunction, fullFormData?: FormData): FormErrorsStep => { const errors: FormErrorsStep = {}; return errors;};
export const validateSchedule13UaeDividends = (data: Schedule13UaeDividendsData, t: TranslationFunction, fullFormData?: FormData): FormErrorsStep => { const errors: FormErrorsStep = {}; return errors;};
export const validateSchedule14ForeignPE = (data: Schedule14ForeignPEData, t: TranslationFunction, fullFormData?: FormData): FormErrorsStep => { const errors: FormErrorsStep = {}; return errors;};
export const validateSchedule15TaxCredits = (data: Schedule15TaxCreditData, t: TranslationFunction, fullFormData?: FormData): FormErrorsStep => { const errors: FormErrorsStep = {}; return errors;};
export const validateSchedule16RelatedParty = (data: Schedule16RelatedPartyData, t: TranslationFunction, fullFormData?: FormData): FormErrorsStep => { const errors: FormErrorsStep = {}; return errors;};
export const validateSchedule16ConnectedPersons = (data: Schedule16ConnectedPersonsData, t: TranslationFunction, fullFormData?: FormData): FormErrorsStep => { const errors: FormErrorsStep = {}; return errors;};

export const validateSchedule17TaxLosses = (data: Schedule17TaxLossData, t: TranslationFunction, fullFormData?: FormData): FormErrorsStep => { 
  const errors: FormErrorsStep = {}; 
  if (fullFormData) {
    const mainFormTaxableIncome = safeParseFloat(fullFormData.taxLiability.taxableIncomeLossBeforeTaxLossUtilisation);
    const utilisedFromSchedule = safeParseFloat(data.taxLossesUtilisedInCurrentPeriodFromSchedule);

    if (mainFormTaxableIncome <= 0 && utilisedFromSchedule > 0) {
      errors.taxLossesUtilisedInCurrentPeriodFromSchedule = t('validation_loss_utilisation_income_negative');
    } else if (mainFormTaxableIncome > 0 && utilisedFromSchedule > (mainFormTaxableIncome * 0.75)) {
      errors.taxLossesUtilisedInCurrentPeriodFromSchedule = t('validation_loss_utilisation_exceeds_cap', {
        utilisedAmount: utilisedFromSchedule.toFixed(2),
        capAmount: (mainFormTaxableIncome * 0.75).toFixed(2)
      });
    }
  }
  return errors;
};

export const validateSchedule17TaxGroupLosses = (data: Schedule17TaxGroupLossData, t: TranslationFunction, fullFormData?: FormData): FormErrorsStep => { 
  const errors: FormErrorsStep = {};
  if (fullFormData) {
    const mainFormTaxableIncome = safeParseFloat(fullFormData.taxLiability.taxableIncomeLossBeforeTaxLossUtilisation);
    const groupLossesUtilised = safeParseFloat(data.taxGroupLossesUtilisedInCurrentPeriod);

    if (mainFormTaxableIncome <= 0 && groupLossesUtilised > 0) {
      errors.taxGroupLossesUtilisedInCurrentPeriod = t('validation_loss_utilisation_income_negative');
    } else if (mainFormTaxableIncome > 0 && groupLossesUtilised > (mainFormTaxableIncome * 0.75)) {
      errors.taxGroupLossesUtilisedInCurrentPeriod = t('validation_loss_utilisation_exceeds_cap', {
        utilisedAmount: groupLossesUtilised.toFixed(2),
        capAmount: (mainFormTaxableIncome * 0.75).toFixed(2)
      });
    }
  }
  return errors;
};
export const validateSchedule18ParticipationExemption = (data: Schedule18ParticipationExemptionData, t: TranslationFunction, fullFormData?: FormData): FormErrorsStep => { const errors: FormErrorsStep = {}; return errors;};
export const validateSchedule19InterestCapping = (data: Schedule19InterestCappingData, t: TranslationFunction, fullFormData?: FormData): FormErrorsStep => { const errors: FormErrorsStep = {}; return errors;};
export const validateSchedule20Relief = (data: Schedule20ReliefData, t: TranslationFunction, fullFormData?: FormData): FormErrorsStep => { const errors: FormErrorsStep = {}; return errors;};
export const validateSchedule21TransitionalRules = (data: Schedule21TransitionalRulesData, t: TranslationFunction, fullFormData?: FormData): FormErrorsStep => { const errors: FormErrorsStep = {}; return errors;};
export const validateSchedule22Other = (data: Schedule22OtherSubScheduleData, t: TranslationFunction, fullFormData?: FormData): FormErrorsStep => { const errors: FormErrorsStep = {}; return errors;};

export const validateSchedule22_4_Attachments = (data: Schedule22_4_AttachmentsData, t: TranslationFunction, formData: FormData): FormErrorsStep => {
  const errors: FormErrorsStep = {};
  const attachmentErrorsArray: FormErrorsStep[] = [];
  let hasAttachmentErrors = false;

  data.attachments.forEach((att, index) => {
    const attError: FormErrorsStep = {};
    let isConditionMet = true;
    if (att.condition) {
        isConditionMet = att.condition(formData);
    }

    if (isConditionMet && att.isMandatory && !att.file && !isNotEmpty(att.reasonIfNotProvided)) {
        attError.file = t('validation_required_file_or_reason', { documentName: t(att.documentKey as any) });
    }
    
    attachmentErrorsArray[index] = attError; 
    if(Object.keys(attError).length > 0) hasAttachmentErrors = true;
  });

  if(hasAttachmentErrors) errors.attachments = attachmentErrorsArray;
  return errors;
};
